import { Mic, Micdrop, MultipleVAD, SileroVAD, VADStatus } from '@micdrop/web';
import { useEffect, useState } from 'react';

interface VadLiveStatusProps {
  /** Current "Niveau de déclenchement" (dB) — mic lamp follows the volume filter. */
  readonly threshold: number;
}

/** Ignore level jitter below the threshold so the lamp does not flicker. */
const MIC_HYSTERESIS_DB = 5;

type LampState = 'on' | 'wait' | 'off';

// Brand palette only (charte graphique) : #21c294, #fffcc4, #254441.
const LAMP_STYLES: Record<LampState, { row: string; dot: string }> = {
  on: {
    row: 'bg-primary/10 text-foreground',
    dot: 'bg-primary shadow-[0_0_0_3px_rgba(33,194,148,0.25)]',
  },
  wait: {
    row: 'bg-[#fffcc4] text-foreground',
    dot: 'bg-[#254441]',
  },
  off: {
    row: 'bg-muted text-muted-foreground',
    dot: 'bg-muted-foreground/30',
  },
};

function getSileroVad(): SileroVAD | null {
  const vad = Micdrop.vad;
  if (!vad) return null;
  const vads = vad.name === 'MultipleVAD' ? (vad as MultipleVAD).vads : [vad];
  return (
    (vads.find(
      instance => instance.name === 'SileroVAD' || instance instanceof SileroVAD,
    ) as SileroVAD) ?? null
  );
}

/**
 * Live lamps shown in the VAD panel so the user can verify, while adjusting
 * the sliders, that (1) the mic picks up sound and (2) the AI recognises a voice.
 */
export function VadLiveStatus({ threshold }: VadLiveStatusProps): React.JSX.Element {
  const [micOn, setMicOn] = useState(false);
  const [aiStatus, setAiStatus] = useState<VADStatus | null>(null);

  // Mic level: same dB metric as the VolumeVAD threshold (stable singleton analyser).
  useEffect(() => {
    let wasOn = false;
    const onVolume = (volumeDb: number) => {
      const next =
        volumeDb >= threshold ? true : volumeDb >= threshold - MIC_HYSTERESIS_DB ? wasOn : false;
      wasOn = next;
      setMicOn(next);
    };
    Mic.on('Volume', onVolume);
    return () => {
      Mic.off('Volume', onVolume);
    };
  }, [threshold]);

  // Silero instance can be replaced when the mic restarts → poll status cheaply.
  useEffect(() => {
    const readStatus = (): void => setAiStatus(getSileroVad()?.status ?? null);
    readStatus();
    const intervalId = window.setInterval(readStatus, 300);
    return () => window.clearInterval(intervalId);
  }, []);

  const micActive = micOn;
  const aiSpeaking = aiStatus === VADStatus.Speaking;
  const aiChecking = aiStatus === VADStatus.MaybeSpeaking;

  const micText = micActive
    ? 'Le micro capte bien le son'
    : "Le micro n'entend rien pour l'instant — parlez pour tester";
  const aiText = aiSpeaking
    ? "L'IA reconnaît votre voix"
    : aiChecking
      ? "L'IA vérifie si c'est une voix"
      : "L'IA attend d'entendre une voix";

  const micState: LampState = micActive ? 'on' : 'off';
  const aiState: LampState = aiSpeaking ? 'on' : aiChecking ? 'wait' : 'off';

  return (
    <div className="flex flex-col gap-2 rounded-xl border bg-card p-3">
      <div
        role="status"
        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${LAMP_STYLES[micState].row}`}
      >
        <span
          aria-hidden="true"
          className={`h-3 w-3 shrink-0 rounded-full ${LAMP_STYLES[micState].dot}`}
        />
        <span>{micText}</span>
      </div>
      <div
        role="status"
        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${LAMP_STYLES[aiState].row}`}
      >
        <span
          aria-hidden="true"
          className={`h-3 w-3 shrink-0 rounded-full ${LAMP_STYLES[aiState].dot}`}
        />
        <span>{aiText}</span>
      </div>
    </div>
  );
}
